/**
 * "Run on a case": pick the case a skill should run against. Before anyone
 * types it offers the cases open in the tab bar (those matching the skill's
 * visas first) and the latest cases for those visas; typing searches like
 * the global search (server-filtered on the caller's practice).
 */
import { Briefcase, FlaskConical } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type FC } from "react";

import { MattersAPI } from "@/api/matters";
import { StatusBadge } from "@/components/ui/status-badge";
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from "@/components/ui/command";
import { Spinner } from "@/components/ui/spinner";
import { useAppTabsStore } from "@/features/appTabs/context/appTabsStore";
import { parseCasePath, type CaseRef } from "@/features/caseIde/routes";
import { matterToResult } from "@/features/globalSearch/searchModel";
import { useGlobalSearch } from "@/features/globalSearch/useGlobalSearch";

import { openCaseSuggestions, type SuggestedCase } from "./caseSuggestions";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** What the run is for, shown in the dialog title. */
  skillTitle: string;
  /** The visas the skill is meant for; their cases are offered first. */
  visas?: string[];
  onPick: (ref: CaseRef, label: string) => void;
  /** "Test": the title and description say it only plans. */
  test?: boolean;
};

const MAX_VISAS = 3;
const PER_VISA = 4;

const CaseRow: FC<{
  item: SuggestedCase;
  onPick: (ref: CaseRef, label: string) => void;
  prefix: string;
}> = ({ item, onPick, prefix }) => (
  <CommandItem
    value={`${prefix}:${item.ref.individualId}`}
    onSelect={() => onPick(item.ref, item.label)}
    data-testid={`case-pick-${item.ref.individualId}`}
  >
    <Briefcase className="size-4 text-fg-3" />
    <div className="min-w-0 flex-1">
      <div className="truncate text-sm">{item.label}</div>
      {item.sublabel ? (
        <div className="truncate text-sm text-fg-3">{item.sublabel}</div>
      ) : null}
    </div>
    {item.matches ? (
      <StatusBadge tone="purple" className="shrink-0">
        Fits This Automation
      </StatusBadge>
    ) : null}
  </CommandItem>
);

export const CasePickerDialog: FC<Props> = ({
  open,
  onOpenChange,
  skillTitle,
  visas,
  onPick,
  test = false
}) => {
  const search = useGlobalSearch(open);
  // Reset once per close; `search` is a fresh object each render, so the
  // reset function is read through a ref rather than listed as a dependency.
  const resetRef = useRef(search.reset);
  resetRef.current = search.reset;
  useEffect(() => {
    if (!open) resetRef.current();
  }, [open]);

  const tabs = useAppTabsStore((s) => s.tabs);
  const recency = useAppTabsStore((s) => s.recency);
  const wanted = useMemo(
    () => (visas ?? []).filter(Boolean).slice(0, MAX_VISAS),
    [visas]
  );
  const openCases = useMemo(
    () => openCaseSuggestions(tabs, recency, wanted),
    [tabs, recency, wanted]
  );

  const [visaCases, setVisaCases] = useState<SuggestedCase[]>([]);
  const wantedKey = wanted.join("|");
  useEffect(() => {
    if (!open || !wantedKey) {
      setVisaCases([]);
      return undefined;
    }
    let cancelled = false;
    Promise.allSettled(
      wantedKey.split("|").map((visa) =>
        MattersAPI.list({
          visa,
          limit: PER_VISA,
          sort_by: "created_at",
          sort_dir: "desc"
        })
      )
    ).then((results) => {
      if (cancelled) return;
      const rows = results.flatMap((r) =>
        r.status === "fulfilled" ? r.value.matters : []
      );
      const out: SuggestedCase[] = [];
      for (const matter of rows) {
        const result = matterToResult(matter);
        const ref = parseCasePath(result.to)?.ref;
        if (ref && !out.some((c) => c.ref.individualId === ref.individualId)) {
          out.push({
            ref,
            label: result.label,
            sublabel: result.sublabel,
            matches: true
          });
        }
      }
      setVisaCases(out);
    });
    return () => {
      cancelled = true;
    };
  }, [open, wantedKey]);

  const openIds = new Set(openCases.map((c) => c.ref.individualId));
  const moreForVisa = visaCases.filter((c) => !openIds.has(c.ref.individualId));

  const cases = search.cases
    .map((r) => ({ result: r, ref: parseCasePath(r.to)?.ref ?? null }))
    .filter((c): c is { result: typeof c.result; ref: CaseRef } => !!c.ref);
  const showEmpty =
    !search.isLoading && search.isServerQuery && cases.length === 0;
  const suggesting = !search.isServerQuery;

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title={test ? `Test ${skillTitle}` : `Run ${skillTitle}`}
      description={
        test
          ? "Pick a case to test on. A test only plans; it changes nothing."
          : "Pick the case to run this automation on"
      }
      className="sm:max-w-130"
    >
      <Command shouldFilter={false}>
        <CommandInput
          placeholder="Search cases by client or petitioner…"
          value={search.query}
          onValueChange={search.setQuery}
          data-testid="case-picker-input"
        />
        {test ? (
          <div
            className="flex items-center gap-2 px-3 pt-2 pb-1 text-sm text-info"
            data-testid="case-picker-test-note"
          >
            <FlaskConical className="size-3.5 shrink-0" />A test plans the work
            and waits; it changes nothing on the case.
          </div>
        ) : null}
        <CommandList>
          {search.isLoading ? (
            <div className="flex items-center gap-2 px-3 py-3 text-sm text-fg-3">
              <Spinner className="size-3" /> Searching…
            </div>
          ) : null}
          {suggesting && openCases.length > 0 ? (
            <CommandGroup heading="Open cases" data-testid="case-picker-open">
              {openCases.map((item) => (
                <CaseRow
                  key={`open:${item.ref.individualId}`}
                  item={item}
                  onPick={onPick}
                  prefix="open"
                />
              ))}
            </CommandGroup>
          ) : null}
          {suggesting && moreForVisa.length > 0 ? (
            <CommandGroup
              heading={`Latest ${wanted.join(", ")} cases`}
              data-testid="case-picker-visa"
            >
              {moreForVisa.map((item) => (
                <CaseRow
                  key={`visa:${item.ref.individualId}`}
                  item={item}
                  onPick={onPick}
                  prefix="visa"
                />
              ))}
            </CommandGroup>
          ) : null}
          {suggesting ? (
            <div className="px-3 py-3 text-sm text-fg-3">
              {openCases.length || moreForVisa.length
                ? "Or type a few letters of the client's name."
                : "Type a few letters of the client's name."}
            </div>
          ) : null}
          {showEmpty ? <CommandEmpty>No matching cases.</CommandEmpty> : null}
          {cases.length ? (
            <CommandGroup heading="Cases">
              {cases.map(({ result, ref }) => (
                <CommandItem
                  key={`case:${result.id}`}
                  value={`${result.label} ${result.sublabel ?? ""} ${
                    result.id
                  }`}
                  onSelect={() => onPick(ref, result.label)}
                  data-testid={`case-pick-${ref.individualId}`}
                >
                  <Briefcase className="size-4 text-fg-3" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm">{result.label}</div>
                    {result.sublabel ? (
                      <div className="truncate text-sm text-fg-3">
                        {result.sublabel}
                      </div>
                    ) : null}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          ) : null}
        </CommandList>
      </Command>
    </CommandDialog>
  );
};
